/**
 * Shop the Look - Single Image with Desktop/Mobile Versions
 * Desktop: Hover to show popup | Mobile: Click to show popup
 * Smart popup positioning with collision detection
 * UPDATED: Variant-level selection support + Mobile popup fix
 * UPDATED: Device-aware bundle targeting for mobile/desktop sidebars
 * UPDATED: AJAX add-to-cart — delegates to window.CartDrawer
 * FIXED:   Bundle population — robust DOM-ready initialization
 */
 
if (!customElements.get('shop-the-look-carousel')) {
  class ShopTheLookCarousel extends HTMLElement {
    constructor() {
      super();
      this.isDragging     = false;
      this.draggedHotspot = null;
      this.bundleProducts = [];
      this.isInitialized  = false;
      this.backdrop       = null;
      this.activePopup    = null;
    }
 
    connectedCallback() {
      this.fullscreen      = this.querySelector('.shop-look-fullscreen');
      this.fullscreenImage = this.querySelector('.shop-look-fullscreen-image');
      this.fullscreenClose = this.querySelector('.shop-look-fullscreen-close');
 
      this.createBackdrop();
      this.setupHotspots();
      this.setupBundle();
      this.setupFullscreen();
      this.setupDragDrop();
      this._initBundle();
 
      this.isInitialized = true;
 
      window.addEventListener('resize', () => {
        this.closeAllPopups();
        this._initBundle();
      });
    }
 
    /**
     * Polls up to 10x (50ms apart) until hotspots with variant IDs exist.
     * Handles theme-editor lazy rendering and slow paints gracefully.
     */
    _initBundle() {
      let attempts = 0;
      const tryUpdate = () => {
        const deviceType   = this.getDeviceType();
        const imageWrapper = this.querySelector(`.shop-look-image-wrapper[data-device="${deviceType}"]`);
        const valid = imageWrapper
          ? [...imageWrapper.querySelectorAll('.shop-look-hotspot')]
              .filter(h => (h.getAttribute('data-variant-id') || '').trim())
          : [];
 
        if (valid.length > 0 || attempts >= 10) {
          this.updateBundleDisplay();
        } else {
          attempts++;
          setTimeout(tryUpdate, 50);
        }
      };
      tryUpdate();
    }
 
    isMobileDevice() { return window.innerWidth <= 749; }
    getDeviceType()  { return this.isMobileDevice() ? 'mobile' : 'desktop'; }
 
    getActiveSidebar() {
      return this.isMobileDevice()
        ? (this.querySelector('.shop-look-sidebar--mobile') || this.querySelector('.shop-look-sidebar'))
        : (this.querySelector('.shop-look-sidebar--desktop') || this.querySelector('.shop-look-sidebar'));
    }
 
    createBackdrop() {
      this.backdrop = document.createElement('div');
      this.backdrop.className = 'shop-look-popup-backdrop';
      this.appendChild(this.backdrop);
      this.backdrop.addEventListener('click', () => this.closeAllPopups());
    }
 
    setupHotspots() {
      this.querySelectorAll('.shop-look-hotspot').forEach(hotspot => {
        const button   = hotspot.querySelector('.shop-look-hotspot-button');
        const popup    = hotspot.querySelector('.shop-look-popup');
        const closeBtn = popup?.querySelector('.shop-look-popup-close');
        const device   = hotspot.getAttribute('data-device');
 
        if (!button || !popup) return;
 
        if (device === 'desktop') {
          let hoverTimeout;
          hotspot.addEventListener('mouseenter', () => {
            if (this.isMobileDevice() || this.isDragging) return;
            clearTimeout(hoverTimeout);
            hoverTimeout = setTimeout(() => {
              this.closeAllPopups();
              this.positionPopup(hotspot, popup);
              popup.classList.add('active');
              button.setAttribute('aria-expanded', 'true');
              this.activePopup = popup;
            }, 100);
          });
          hotspot.addEventListener('mouseleave', () => {
            if (this.isMobileDevice()) return;
            clearTimeout(hoverTimeout);
            hoverTimeout = setTimeout(() => {
              if (!popup.matches(':hover')) {
                popup.classList.remove('active');
                button.setAttribute('aria-expanded', 'false');
                this.activePopup = null;
              }
            }, 200);
          });
          popup.addEventListener('mouseenter', () => { if (!this.isMobileDevice()) clearTimeout(hoverTimeout); });
          popup.addEventListener('mouseleave', () => {
            if (this.isMobileDevice()) return;
            popup.classList.remove('active');
            button.setAttribute('aria-expanded', 'false');
            this.activePopup = null;
          });
        }
 
        if (device === 'mobile') {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const isActive = popup.classList.contains('active');
            this.closeAllPopups();
            if (!isActive) {
              this.positionPopup(hotspot, popup);
              popup.classList.add('active');
              this.backdrop.classList.add('active');
              button.setAttribute('aria-expanded', 'true');
              document.body.style.overflow = 'hidden';
              this.activePopup = popup;
            }
          });
        }
 
        closeBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.closeAllPopups();
        });
 
        popup.addEventListener('click', (e) => e.stopPropagation());
      });
 
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeAllPopups();
      });
      document.addEventListener('click', (e) => {
        if (this.activePopup &&
            !e.target.closest('.shop-look-hotspot') &&
            !e.target.closest('.shop-look-popup')) {
          this.closeAllPopups();
        }
      });
    }
 
    positionPopup(hotspot, popup) {
      popup.style.position  = 'fixed';
      popup.style.top       = '';
      popup.style.bottom    = '';
      popup.style.left      = '';
      popup.style.right     = '';
      popup.style.transform = '';
 
      if (this.isMobileDevice()) {
        popup.style.top       = '50%';
        popup.style.left      = '50%';
        popup.style.transform = 'translate(-50%, -50%)';
        popup.style.maxHeight = '80vh';
        popup.style.overflowY = 'auto';
        return;
      }
 
      const rect     = hotspot.getBoundingClientRect();
      const xPercent = parseFloat(hotspot.getAttribute('data-x')) || 50;
      const yPercent = parseFloat(hotspot.getAttribute('data-y')) || 50;
      const popupW   = popup.offsetWidth || 380;
      const vpW      = window.innerWidth;
      const vpH      = window.innerHeight;
 
      let left = xPercent > 70 ? rect.left - popupW - 10 : rect.right + 10;
      if (left + popupW > vpW) left = rect.left - popupW - 10;
      if (left < 0)            left = rect.right + 10;
 
      let top = yPercent < 30 ? rect.top
              : yPercent > 70 ? rect.bottom - popup.offsetHeight
              : rect.top + rect.height / 2 - popup.offsetHeight / 2;
 
      if (top + popup.offsetHeight > vpH) top = vpH - popup.offsetHeight - 20;
      if (top < 20) top = 20;
 
      popup.style.left = left + 'px';
      popup.style.top  = top  + 'px';
    }
 
    closeAllPopups() {
      this.querySelectorAll('.shop-look-popup').forEach(popup => {
        popup.classList.remove('active');
        const btn = popup.closest('.shop-look-hotspot')?.querySelector('.shop-look-hotspot-button');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
      if (this.backdrop) this.backdrop.classList.remove('active');
      document.body.style.overflow = '';
      this.activePopup = null;
    }
 
    updateBundleDisplay() {
      const deviceType              = this.getDeviceType();
      const activeSidebar           = this.getActiveSidebar();
      const bundleProductsContainer = activeSidebar?.querySelector('[data-bundle-products]');
      const bundleBtn               = activeSidebar?.querySelector('[data-bundle-add]');
 
      if (!bundleProductsContainer || !bundleBtn) return;
 
      const activeImageWrapper = this.querySelector(`.shop-look-image-wrapper[data-device="${deviceType}"]`);
      if (!activeImageWrapper) {
        bundleProductsContainer.innerHTML = '<div class="shop-look-bundle-empty">No products selected</div>';
        bundleBtn.disabled = true;
        return;
      }
 
      this.bundleProducts = [];
      activeImageWrapper.querySelectorAll('.shop-look-hotspot').forEach(hotspot => {
        if (hotspot.getAttribute('data-show-in-bundle') === 'false') return;
        const variantId = (hotspot.getAttribute('data-variant-id') || '').trim();
        if (!variantId) return;
        const imgEl   = hotspot.querySelector('.shop-look-popup-product-image img');
        const titleEl = hotspot.querySelector('.shop-look-popup-product-title');
        const priceEl = hotspot.querySelector('.shop-look-popup-product-price');
        if (!titleEl) return;
        this.bundleProducts.push({
          variantId,
          image : imgEl?.getAttribute('src') || '',
          title : titleEl.textContent.trim(),
          price : priceEl?.textContent.trim() || ''
        });
      });
 
      this.renderBundleProducts(bundleProductsContainer, bundleBtn);
    }
 
    renderBundleProducts(container, button) {
      if (this.bundleProducts.length === 0) {
        container.innerHTML = '<div class="shop-look-bundle-empty">No products selected</div>';
        button.disabled = true;
        return;
      }
      container.innerHTML = this.bundleProducts.map(p => `
        <div class="shop-look-bundle-product">
          <div class="shop-look-bundle-product-image">
            ${p.image ? `<img src="${p.image}" alt="${this.escapeHtml(p.title)}" loading="lazy">` : ''}
          </div>
          <div class="shop-look-bundle-product-info">
            <div class="shop-look-bundle-product-title">${this.escapeHtml(p.title)}</div>
            <div class="shop-look-bundle-product-price">${p.price}</div>
          </div>
        </div>
      `).join('');
      button.disabled = false;
    }
 
    escapeHtml(str) {
      const d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }
 
    setupBundle() {
      this.querySelectorAll('[data-bundle-add]').forEach(btn => {
        btn.setAttribute('data-original-text', btn.textContent);
        btn.addEventListener('click', () => this.handleBundleAdd(btn));
      });
    }
 
    handleBundleAdd(clickedBtn) {
      const deviceType         = this.getDeviceType();
      const activeImageWrapper = this.querySelector(`.shop-look-image-wrapper[data-device="${deviceType}"]`);
      if (!activeImageWrapper) return;
 
      const items = [];
      activeImageWrapper.querySelectorAll('.shop-look-hotspot').forEach(hotspot => {
        if (hotspot.getAttribute('data-show-in-bundle') === 'false') return;
        const variantId = (hotspot.getAttribute('data-variant-id') || '').trim();
        if (variantId) items.push({ id: variantId, quantity: 1 });
      });
 
      if (!items.length) return;
 
      const originalText     = clickedBtn.getAttribute('data-original-text') || 'Add all to cart';
      clickedBtn.disabled    = true;
      clickedBtn.textContent = 'Adding\u2026';
 
      fetch('/cart/add.js', {
        method      : 'POST',
        credentials : 'same-origin',
        headers     : { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body        : JSON.stringify({ items })
      })
      .then(res => {
        if (!res.ok) return res.json().then(err => Promise.reject(err));
        return res.json();
      })
      .then(() => {
        clickedBtn.textContent = '\u2713 Added!';
        setTimeout(() => {
          clickedBtn.textContent = originalText;
          clickedBtn.disabled    = false;
        }, 2000);
 
        /* Fetch fresh cart count, update badge, then open drawer */
        fetch('/cart.js', {
          credentials : 'same-origin',
          headers     : { 'X-Requested-With': 'XMLHttpRequest' }
        })
        .then(r => r.json())
        .then(cart => {
          if (window.CartDrawer) {
            window.CartDrawer.syncBadge(cart.item_count);
            window.CartDrawer.open();
          }
        });
      })
      .catch(err => {
        console.error('[ShopTheLook] Cart error:', err);
        clickedBtn.textContent = originalText;
        clickedBtn.disabled    = false;
        alert(err.description || 'Error adding products to cart. Please try again.');
      });
    }
 
    setupFullscreen() {
      this.querySelectorAll('.shop-look-zoom-icon').forEach(icon => {
        icon.addEventListener('click', (e) => {
          e.stopPropagation();
          const device  = icon.getAttribute('data-zoom-device');
          const wrapper = this.querySelector(`.shop-look-image-wrapper[data-device="${device}"]`);
          const img     = wrapper?.querySelector('.shop-look-image');
          if (img) {
            this.fullscreenImage.src = img.src;
            this.fullscreenImage.alt = img.alt;
            this.fullscreen.classList.add('active');
            document.body.style.overflow = 'hidden';
          }
        });
      });
      this.fullscreenClose.addEventListener('click', () => {
        this.fullscreen.classList.remove('active');
        document.body.style.overflow = '';
      });
      this.fullscreen.addEventListener('click', (e) => {
        if (e.target === this.fullscreen) {
          this.fullscreen.classList.remove('active');
          document.body.style.overflow = '';
        }
      });
    }
 
    setupDragDrop() {
      if (!Shopify.designMode) return;
      const onMove = (e) => {
        if (!this.isDragging || !this.draggedHotspot) return;
        const device  = this.draggedHotspot.getAttribute('data-device');
        const wrapper = this.querySelector(`.shop-look-image-wrapper[data-device="${device}"]`);
        if (!wrapper) return;
        const rect = wrapper.getBoundingClientRect();
        const x    = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width)  * 100));
        const y    = Math.max(0, Math.min(100, ((e.clientY - rect.top)  / rect.height) * 100));
        this.draggedHotspot.style.left = x + '%';
        this.draggedHotspot.style.top  = y + '%';
        this.draggedHotspot.setAttribute('data-x', x.toFixed(1));
        this.draggedHotspot.setAttribute('data-y', y.toFixed(1));
      };
      const onUp = () => {
        if (this.isDragging && this.draggedHotspot) {
          this.draggedHotspot.classList.remove('dragging');
          this.isDragging     = false;
          this.draggedHotspot = null;
        }
      };
      this.querySelectorAll('.shop-look-hotspot').forEach(hotspot => {
        hotspot.addEventListener('mousedown', (e) => {
          if (e.target.closest('.shop-look-hotspot-button')) {
            this.isDragging     = true;
            this.draggedHotspot = hotspot;
            hotspot.classList.add('dragging');
            this.closeAllPopups();
            e.preventDefault();
          }
        });
      });
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup',   onUp);
    }
  }
 
  customElements.define('shop-the-look-carousel', ShopTheLookCarousel);
}